import type { StoreState } from '../../storeState';
import { appManager, AppState } from '../manager';
import type {
  InspectAssessmentCandidatePositionEvidence,
  InspectAssessmentCandidatePositionsRequest,
  InspectAssessmentInstrument,
  InspectAssessmentKlass,
  InspectAssessmentKlassesRequest,
  InspectAssessmentOptimalPortfolio,
  InspectAssessmentOptimalPortfolioRequest,
  InspectAssessmentOption,
  InspectAssessmentPeriods,
  InspectAssessmentPeriodsBatch,
  InspectAssessmentPeriodsBatchRequest,
  InspectAssessmentSliderDirection,
  InspectAssessmentSliderDirectionRequest,
  InspectAssessmentUser,
  InspectAssessmentUserAssignments,
  InspectAssessmentUserAssignmentsCacheEntry,
  InspectAssessmentUserAssignmentsRequest,
  InspectAssessmentUsersRequest,
  InspectAssessmentsRequest,
} from './types';

const inspectAssessmentErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(String).join(' ');
    for (const value of Object.values(error as Record<string, unknown>)) {
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.map(String).join(' ');
    }
  }
  return fallback;
};

export const selectInspectAssessmentState = (state: StoreState) =>
  state.app.inspect_assessment;

// Each picker list is fetched for one scope: the assessment it is narrowed to
// and whether rows without data are included. The key names that scope, so a
// late response for an abandoned scope can be dropped and the page can tell
// when it must ask again.
const inspectAssessmentEmptinessKey = (includeEmpty?: 1) =>
  includeEmpty ? 'all' : 'with-data';

export const inspectAssessmentsRequestKey = (
  payload: InspectAssessmentsRequest,
) => inspectAssessmentEmptinessKey(payload.include_empty);

export const inspectAssessmentKlassesRequestKey = (
  payload: InspectAssessmentKlassesRequest,
) =>
  `${payload.assessment_id ?? 'any'}:${inspectAssessmentEmptinessKey(
    payload.include_empty,
  )}`;

export const inspectAssessmentUsersRequestKey = (
  payload: InspectAssessmentUsersRequest,
) =>
  `${payload.klass_id}:${payload.assessment_id ?? 'any'}:${inspectAssessmentEmptinessKey(
    payload.include_empty,
  )}`;

export const inspectAssessmentPeriodPriceKey = (
  assessmentId: number,
  periodIndex: number,
  instrumentId: number,
) => `${assessmentId}:${periodIndex}:${instrumentId}`;

export const inspectAssessmentUserAssignmentsContextKey = (
  assessmentId: number,
  klassId: number,
  userId: number,
) => `${assessmentId}:${klassId}:${userId}`;

export const inspectAssessmentUserAssignmentsRequestKey = (
  payload: InspectAssessmentUserAssignmentsRequest,
) =>
  `${inspectAssessmentUserAssignmentsContextKey(
    payload.assessment_id,
    payload.klass_id,
    payload.user_id,
  )}:${payload.session_id ?? 'auto'}`;

export const inspectAssessmentSliderDirectionRequestKey = (
  payload: InspectAssessmentSliderDirectionRequest,
) =>
  [
    payload.assessment_id,
    payload.session_id,
    payload.link_id,
    payload.player_id,
  ].join(':');

export const inspectAssessmentOptimalPortfolioRequestKey = (
  payload: InspectAssessmentOptimalPortfolioRequest,
) => `${payload.assessment_id}:${payload.link_id ?? 'shared'}`;

export const inspectAssessmentCandidatePositionsRequestKey = (
  payload: InspectAssessmentCandidatePositionsRequest,
) =>
  [
    payload.assessment_id,
    payload.session_id,
    payload.link_id,
    payload.player_id,
  ].join(':');

const createInspectAssessmentUserAssignmentsCacheEntry =
  (): InspectAssessmentUserAssignmentsCacheEntry => ({
    attempts: [],
    assignmentsBySessionId: {},
    orphanedAssignmentCountBySessionId: {},
    pillarOnePeriodsBySessionId: {},
    pillarOneInstrumentCodesBySessionId: {},
    fetchingByRequestKey: {},
    fetchedByRequestKey: {},
    errorsByRequestKey: {},
  });

const ensureInspectAssessmentUserAssignmentsCacheEntry = (
  state: AppState,
  contextKey: string,
) => {
  if (!state.inspect_assessment.userAssignmentsByContextKey[contextKey]) {
    state.inspect_assessment.userAssignmentsByContextKey[contextKey] =
      createInspectAssessmentUserAssignmentsCacheEntry();
  }
  return state.inspect_assessment.userAssignmentsByContextKey[contextKey];
};

const setInspectAssessmentSelectedUser = (state: AppState, userId?: number) => {
  state.inspect_assessment.selectedUserId = userId;
  const assessmentId = state.inspect_assessment.selectedAssessmentId;
  const klassId = state.inspect_assessment.selectedKlassId;
  if (
    assessmentId === undefined ||
    klassId === undefined ||
    userId === undefined
  )
    return;

  const contextKey = inspectAssessmentUserAssignmentsContextKey(
    assessmentId,
    klassId,
    userId,
  );
  const entry =
    state.inspect_assessment.userAssignmentsByContextKey[contextKey];
  if (!entry) return;
  const requestKey = inspectAssessmentUserAssignmentsRequestKey({
    assessment_id: assessmentId,
    klass_id: klassId,
    user_id: userId,
    session_id: entry.selectedSessionId,
  });
  if (entry.errorsByRequestKey[requestKey]) {
    delete entry.errorsByRequestKey[requestKey];
    delete entry.fetchedByRequestKey[requestKey];
  }
};

const inspectAssessmentBatchIds = (value?: string) =>
  value ? value.split(',').map(Number).filter(Number.isFinite) : [];

const inspectAssessmentPeriodBatchRequestKey = (
  payload: InspectAssessmentPeriodsBatchRequest,
) =>
  [
    payload.assessment_id,
    payload.period_indices,
    payload.instrument_ids ?? '',
    payload.include_news,
  ].join(':');

const clearInspectAssessmentBatchFetching = (
  state: AppState,
  payload: InspectAssessmentPeriodsBatchRequest,
) => {
  const periodIndices = inspectAssessmentBatchIds(payload.period_indices);
  const instrumentIds = inspectAssessmentBatchIds(payload.instrument_ids);
  periodIndices.forEach(periodIndex => {
    if (payload.include_news === 'true') {
      delete state.inspect_assessment.periodNewsBatchFetchingByIndex[
        periodIndex
      ];
    }
    instrumentIds.forEach(instrumentId => {
      delete state.inspect_assessment.periodPricesBatchFetchingByKey[
        inspectAssessmentPeriodPriceKey(
          payload.assessment_id,
          periodIndex,
          instrumentId,
        )
      ];
    });
  });
};

export const fetchInspectAssessments = appManager.createApi<
  InspectAssessmentsRequest,
  InspectAssessmentOption[],
  AppState
>('FETCH_INSPECT_ASSESSMENTS', {
  path: '/inspect_assessment/assessments',
  method: 'GET',
  startReducer(state, payload) {
    state.inspect_assessment.assessmentsFetching = true;
    state.inspect_assessment.assessmentsError = undefined;
    state.inspect_assessment.assessmentsRequestKey =
      inspectAssessmentsRequestKey(payload);
  },
  successReducer(state, result, payload) {
    if (
      state.inspect_assessment.assessmentsRequestKey !==
      inspectAssessmentsRequestKey(payload)
    )
      return;
    state.inspect_assessment.assessments = result;
    state.inspect_assessment.assessmentsFetching = false;
    state.inspect_assessment.assessmentsFetched = true;
  },
  failReducer(state, error, payload) {
    if (
      state.inspect_assessment.assessmentsRequestKey !==
      inspectAssessmentsRequestKey(payload)
    )
      return;
    state.inspect_assessment.assessmentsFetching = false;
    state.inspect_assessment.assessmentsError = inspectAssessmentErrorMessage(
      error,
      'Assessments could not be loaded.',
    );
  },
});

export const fetchInspectAssessmentKlasses = appManager.createApi<
  InspectAssessmentKlassesRequest,
  InspectAssessmentKlass[],
  AppState
>('FETCH_INSPECT_ASSESSMENT_KLASSES', {
  path: '/inspect_assessment/klasses',
  method: 'GET',
  startReducer(state, payload) {
    state.inspect_assessment.klassesFetching = true;
    state.inspect_assessment.klassesError = undefined;
    state.inspect_assessment.klassesRequestKey =
      inspectAssessmentKlassesRequestKey(payload);
  },
  successReducer(state, result, payload) {
    if (
      state.inspect_assessment.klassesRequestKey !==
      inspectAssessmentKlassesRequestKey(payload)
    )
      return;
    state.inspect_assessment.klasses = result;
    state.inspect_assessment.klassesFetching = false;
    state.inspect_assessment.klassesFetched = true;
  },
  failReducer(state, error, payload) {
    if (
      state.inspect_assessment.klassesRequestKey !==
      inspectAssessmentKlassesRequestKey(payload)
    )
      return;
    state.inspect_assessment.klassesFetching = false;
    // The rows in hand answered the scope we walked away from, and the scope
    // we asked for has nothing to show for itself. Keeping them would leave
    // the dropdown offering another assessment's classes, with that
    // assessment's counts, under an error saying the list could not load —
    // and picking one of them would scope the page to a class the selected
    // assessment may never have been sat in. The request key stays where it
    // is, so the failed scope is not re-asked on every render.
    state.inspect_assessment.klasses = [];
    state.inspect_assessment.klassesFetched = false;
    state.inspect_assessment.klassesError = inspectAssessmentErrorMessage(
      error,
      'Klasses could not be loaded.',
    );
  },
});

export const fetchInspectAssessmentUsers = appManager.createApi<
  InspectAssessmentUsersRequest,
  InspectAssessmentUser[],
  AppState
>('FETCH_INSPECT_ASSESSMENT_USERS', {
  path: '/inspect_assessment/users',
  method: 'GET',
  startReducer(state, payload) {
    state.inspect_assessment.usersFetching = true;
    state.inspect_assessment.usersError = undefined;
    state.inspect_assessment.usersKlassId = payload.klass_id;
    state.inspect_assessment.usersRequestKey =
      inspectAssessmentUsersRequestKey(payload);
  },
  successReducer(state, result, payload) {
    if (
      state.inspect_assessment.usersRequestKey !==
      inspectAssessmentUsersRequestKey(payload)
    )
      return;
    state.inspect_assessment.users = result;
    state.inspect_assessment.usersFetching = false;
    if (
      !result.some(user => user.id === state.inspect_assessment.selectedUserId)
    ) {
      setInspectAssessmentSelectedUser(state, result[0]?.id);
    }
  },
  failReducer(state, error, payload) {
    if (
      state.inspect_assessment.usersRequestKey !==
      inspectAssessmentUsersRequestKey(payload)
    )
      return;
    state.inspect_assessment.usersFetching = false;
    state.inspect_assessment.usersError = inspectAssessmentErrorMessage(
      error,
      'Users could not be loaded.',
    );
  },
});

export const selectInspectAssessmentKlass = appManager.createLocalEvent(
  'SELECT_INSPECT_ASSESSMENT_KLASS',
  (state: AppState, klassId?: number) => {
    state.inspect_assessment.selectedKlassId = klassId;
    state.inspect_assessment.users = [];
    state.inspect_assessment.usersFetching = false;
    state.inspect_assessment.usersKlassId = undefined;
    state.inspect_assessment.usersRequestKey = undefined;
    state.inspect_assessment.usersError = undefined;
    state.inspect_assessment.selectedUserId = undefined;
  },
);

export const selectInspectAssessmentUser = appManager.createLocalEvent(
  'SELECT_INSPECT_ASSESSMENT_USER',
  (state: AppState, userId?: number) => {
    setInspectAssessmentSelectedUser(state, userId);
  },
);

export const selectInspectAssessmentUserSession = appManager.createLocalEvent(
  'SELECT_INSPECT_ASSESSMENT_USER_SESSION',
  (state: AppState, sessionId?: number) => {
    const assessmentId = state.inspect_assessment.selectedAssessmentId;
    const klassId = state.inspect_assessment.selectedKlassId;
    const userId = state.inspect_assessment.selectedUserId;
    if (
      assessmentId === undefined ||
      klassId === undefined ||
      userId === undefined
    )
      return;
    const contextKey = inspectAssessmentUserAssignmentsContextKey(
      assessmentId,
      klassId,
      userId,
    );
    const entry = ensureInspectAssessmentUserAssignmentsCacheEntry(
      state,
      contextKey,
    );
    if (sessionId === undefined) {
      delete entry.selectedSessionId;
    } else {
      entry.selectedSessionId = sessionId;
    }
  },
);

export const fetchInspectAssessmentUserAssignments = appManager.createApi<
  InspectAssessmentUserAssignmentsRequest,
  InspectAssessmentUserAssignments,
  AppState
>('FETCH_INSPECT_ASSESSMENT_USER_ASSIGNMENTS', {
  path: '/inspect_assessment/user_assignments',
  method: 'GET',
  startReducer(state, payload) {
    const contextKey = inspectAssessmentUserAssignmentsContextKey(
      payload.assessment_id,
      payload.klass_id,
      payload.user_id,
    );
    const requestKey = inspectAssessmentUserAssignmentsRequestKey(payload);
    const entry = ensureInspectAssessmentUserAssignmentsCacheEntry(
      state,
      contextKey,
    );
    entry.fetchingByRequestKey[requestKey] = true;
    entry.fetchedByRequestKey[requestKey] = false;
    delete entry.errorsByRequestKey[requestKey];
  },
  successReducer(state, result, payload) {
    const contextKey = inspectAssessmentUserAssignmentsContextKey(
      payload.assessment_id,
      payload.klass_id,
      payload.user_id,
    );
    const requestKey = inspectAssessmentUserAssignmentsRequestKey(payload);
    const entry = ensureInspectAssessmentUserAssignmentsCacheEntry(
      state,
      contextKey,
    );
    entry.attempts = result.attempts;
    entry.fetchingByRequestKey[requestKey] = false;
    entry.fetchedByRequestKey[requestKey] = true;
    delete entry.errorsByRequestKey[requestKey];

    const selectedSessionId = result.selected_attempt?.session_id;
    if (selectedSessionId !== undefined) {
      entry.assignmentsBySessionId[selectedSessionId] = result.assignments;
      entry.orphanedAssignmentCountBySessionId[selectedSessionId] =
        result.orphaned_assignment_count;
      entry.pillarOnePeriodsBySessionId[selectedSessionId] =
        result.pillar_1_periods;
      entry.pillarOneInstrumentCodesBySessionId[selectedSessionId] =
        result.pillar_1_instrument_codes;

      const selectedRequestKey = inspectAssessmentUserAssignmentsRequestKey({
        ...payload,
        session_id: selectedSessionId,
      });
      entry.fetchingByRequestKey[selectedRequestKey] = false;
      entry.fetchedByRequestKey[selectedRequestKey] = true;
      delete entry.errorsByRequestKey[selectedRequestKey];

      if (
        payload.session_id === undefined &&
        entry.selectedSessionId === undefined
      ) {
        entry.selectedSessionId = selectedSessionId;
      }
    }
  },
  failReducer(state, error, payload) {
    const contextKey = inspectAssessmentUserAssignmentsContextKey(
      payload.assessment_id,
      payload.klass_id,
      payload.user_id,
    );
    const requestKey = inspectAssessmentUserAssignmentsRequestKey(payload);
    const entry = ensureInspectAssessmentUserAssignmentsCacheEntry(
      state,
      contextKey,
    );
    entry.fetchingByRequestKey[requestKey] = false;
    entry.fetchedByRequestKey[requestKey] = true;
    entry.errorsByRequestKey[requestKey] = inspectAssessmentErrorMessage(
      error,
      'User news assignments could not be loaded.',
    );
  },
});

export const fetchInspectAssessmentSliderDirection = appManager.createApi<
  InspectAssessmentSliderDirectionRequest,
  InspectAssessmentSliderDirection,
  AppState
>('FETCH_INSPECT_ASSESSMENT_SLIDER_DIRECTION', {
  path: '/inspect_assessment/slider_direction',
  method: 'GET',
  startReducer(state, payload) {
    const requestKey = inspectAssessmentSliderDirectionRequestKey(payload);
    state.inspect_assessment.sliderDirectionFetchingByRequestKey[requestKey] =
      true;
    state.inspect_assessment.sliderDirectionFetchedByRequestKey[requestKey] =
      false;
    delete state.inspect_assessment.sliderDirectionErrorByRequestKey[
      requestKey
    ];
  },
  successReducer(state, result, payload) {
    const requestKey = inspectAssessmentSliderDirectionRequestKey(payload);
    state.inspect_assessment.sliderDirectionFetchingByRequestKey[requestKey] =
      false;
    state.inspect_assessment.sliderDirectionFetchedByRequestKey[requestKey] =
      true;
    if (
      result.assessment_id !== payload.assessment_id ||
      result.session_id !== payload.session_id ||
      result.link_id !== payload.link_id ||
      result.player_id !== payload.player_id
    ) {
      state.inspect_assessment.sliderDirectionErrorByRequestKey[requestKey] =
        'The slider audit returned data for a different assessment attempt.';
      return;
    }
    state.inspect_assessment.sliderDirectionByRequestKey[requestKey] = result;
    delete state.inspect_assessment.sliderDirectionErrorByRequestKey[
      requestKey
    ];
  },
  failReducer(state, error, payload) {
    const requestKey = inspectAssessmentSliderDirectionRequestKey(payload);
    state.inspect_assessment.sliderDirectionFetchingByRequestKey[requestKey] =
      false;
    state.inspect_assessment.sliderDirectionFetchedByRequestKey[requestKey] =
      true;
    state.inspect_assessment.sliderDirectionErrorByRequestKey[requestKey] =
      inspectAssessmentErrorMessage(
        error,
        'Slider score details could not be loaded.',
      );
  },
});

export const fetchInspectAssessmentOptimalPortfolio = appManager.createApi<
  InspectAssessmentOptimalPortfolioRequest,
  InspectAssessmentOptimalPortfolio,
  AppState
>('FETCH_INSPECT_ASSESSMENT_OPTIMAL_PORTFOLIO', {
  path: '/inspect_assessment/optimal_portfolio',
  method: 'GET',
  startReducer(state, payload) {
    const requestKey = inspectAssessmentOptimalPortfolioRequestKey(payload);
    state.inspect_assessment.optimalPortfolioFetchingByRequestKey[requestKey] =
      true;
    state.inspect_assessment.optimalPortfolioFetchedByRequestKey[requestKey] =
      false;
    delete state.inspect_assessment.optimalPortfolioErrorByRequestKey[
      requestKey
    ];
  },
  successReducer(state, result, payload) {
    const requestKey = inspectAssessmentOptimalPortfolioRequestKey(payload);
    state.inspect_assessment.optimalPortfolioFetchingByRequestKey[requestKey] =
      false;
    state.inspect_assessment.optimalPortfolioFetchedByRequestKey[requestKey] =
      true;
    if (
      result.assessment_id !== payload.assessment_id ||
      result.link_id !== (payload.link_id ?? null)
    ) {
      state.inspect_assessment.optimalPortfolioErrorByRequestKey[requestKey] =
        'The optimal portfolio response belongs to a different assessment session.';
      return;
    }
    state.inspect_assessment.optimalPortfolioByRequestKey[requestKey] = result;
    delete state.inspect_assessment.optimalPortfolioErrorByRequestKey[
      requestKey
    ];
  },
  failReducer(state, error, payload) {
    const requestKey = inspectAssessmentOptimalPortfolioRequestKey(payload);
    state.inspect_assessment.optimalPortfolioFetchingByRequestKey[requestKey] =
      false;
    state.inspect_assessment.optimalPortfolioFetchedByRequestKey[requestKey] =
      true;
    state.inspect_assessment.optimalPortfolioErrorByRequestKey[requestKey] =
      inspectAssessmentErrorMessage(
        error,
        'Optimal portfolio exposures and instrument limits could not be loaded.',
      );
  },
});

export const ensureInspectAssessmentCandidatePositions = appManager.createApi<
  InspectAssessmentCandidatePositionsRequest,
  InspectAssessmentCandidatePositionEvidence,
  AppState
>('ENSURE_INSPECT_ASSESSMENT_CANDIDATE_POSITIONS', {
  path: '/inspect_assessment/candidate_positions',
  method: 'POST',
  startReducer(state, payload) {
    const requestKey = inspectAssessmentCandidatePositionsRequestKey(payload);
    state.inspect_assessment.candidatePositionsFetchingByRequestKey[
      requestKey
    ] = true;
    state.inspect_assessment.candidatePositionsFetchedByRequestKey[requestKey] =
      false;
    delete state.inspect_assessment.candidatePositionsErrorByRequestKey[
      requestKey
    ];
  },
  successReducer(state, result, payload) {
    const requestKey = inspectAssessmentCandidatePositionsRequestKey(payload);
    state.inspect_assessment.candidatePositionsFetchingByRequestKey[
      requestKey
    ] = false;
    state.inspect_assessment.candidatePositionsFetchedByRequestKey[requestKey] =
      true;
    if (
      result.assessment_id !== payload.assessment_id ||
      result.session_id !== payload.session_id ||
      result.link_id !== payload.link_id ||
      result.player_id !== payload.player_id
    ) {
      state.inspect_assessment.candidatePositionsErrorByRequestKey[requestKey] =
        'The Candidate Position response belongs to a different assessment attempt.';
      return;
    }
    state.inspect_assessment.candidatePositionsByRequestKey[requestKey] =
      result;
    delete state.inspect_assessment.candidatePositionsErrorByRequestKey[
      requestKey
    ];
  },
  failReducer(state, error, payload) {
    const requestKey = inspectAssessmentCandidatePositionsRequestKey(payload);
    state.inspect_assessment.candidatePositionsFetchingByRequestKey[
      requestKey
    ] = false;
    state.inspect_assessment.candidatePositionsFetchedByRequestKey[requestKey] =
      true;
    state.inspect_assessment.candidatePositionsErrorByRequestKey[requestKey] =
      inspectAssessmentErrorMessage(
        error,
        'Candidate Position evidence could not be loaded.',
      );
  },
});

export const selectInspectAssessment = appManager.createLocalEvent(
  'SELECT_INSPECT_ASSESSMENT',
  (state: AppState, assessmentId?: number) => {
    state.inspect_assessment.selectedAssessmentId = assessmentId;
    state.inspect_assessment.instruments = [];
    state.inspect_assessment.instrumentsFetching = false;
    state.inspect_assessment.instrumentsAssessmentId = undefined;
    state.inspect_assessment.instrumentsError = undefined;
    state.inspect_assessment.selectedInstrumentId = undefined;
    state.inspect_assessment.periods = [];
    state.inspect_assessment.periodsFetching = false;
    state.inspect_assessment.periodsAssessmentId = undefined;
    state.inspect_assessment.periodsError = undefined;
    state.inspect_assessment.periodsBlueprint = undefined;
    state.inspect_assessment.selectedPeriodIndex = undefined;
    state.inspect_assessment.periodNewsByIndex = {};
    state.inspect_assessment.periodNewsFetching = false;
    state.inspect_assessment.periodNewsAssessmentId = undefined;
    state.inspect_assessment.periodNewsPeriodIndex = undefined;
    state.inspect_assessment.periodNewsError = undefined;
    state.inspect_assessment.periodNewsBatchFetchingByIndex = {};
    state.inspect_assessment.periodPricesByKey = {};
    state.inspect_assessment.periodPricesFetchingByKey = {};
    state.inspect_assessment.periodPricesBatchFetchingByKey = {};
    state.inspect_assessment.periodPricesErrorByKey = {};
    if (!state.inspect_assessment.periodBatchFetching) {
      state.inspect_assessment.periodBatchAssessmentId = undefined;
      state.inspect_assessment.periodBatchRequestKey = undefined;
    }
    state.inspect_assessment.periodBatchError = undefined;
  },
);

export const fetchInspectAssessmentInstruments = appManager.createApi<
  { assessment_id: number },
  InspectAssessmentInstrument[],
  AppState
>('FETCH_INSPECT_ASSESSMENT_INSTRUMENTS', {
  path: '/inspect_assessment/instruments',
  method: 'GET',
  startReducer(state, payload) {
    state.inspect_assessment.instrumentsFetching = true;
    state.inspect_assessment.instrumentsAssessmentId = payload.assessment_id;
    state.inspect_assessment.instrumentsError = undefined;
  },
  successReducer(state, result, payload) {
    if (
      state.inspect_assessment.instrumentsAssessmentId !== payload.assessment_id
    )
      return;
    state.inspect_assessment.instruments = result;
    state.inspect_assessment.instrumentsFetching = false;
    state.inspect_assessment.selectedInstrumentId = result[0]?.id;
  },
  failReducer(state, error, payload) {
    if (
      state.inspect_assessment.instrumentsAssessmentId !== payload.assessment_id
    )
      return;
    state.inspect_assessment.instrumentsFetching = false;
    state.inspect_assessment.instrumentsError = inspectAssessmentErrorMessage(
      error,
      'Instruments could not be loaded.',
    );
  },
});

export const selectInspectAssessmentInstrument = appManager.createLocalEvent(
  'SELECT_INSPECT_ASSESSMENT_INSTRUMENT',
  (state: AppState, instrumentId?: number) => {
    state.inspect_assessment.selectedInstrumentId = instrumentId;
  },
);

export const fetchInspectAssessmentPeriods = appManager.createApi<
  { assessment_id: number; period_index?: number },
  InspectAssessmentPeriods,
  AppState
>('FETCH_INSPECT_ASSESSMENT_PERIODS', {
  path: '/inspect_assessment/periods',
  method: 'GET',
  startReducer(state, payload) {
    state.inspect_assessment.periodsFetching = true;
    state.inspect_assessment.periodsAssessmentId = payload.assessment_id;
    state.inspect_assessment.periodsError = undefined;
  },
  successReducer(state, result, payload) {
    if (state.inspect_assessment.periodsAssessmentId !== payload.assessment_id)
      return;
    state.inspect_assessment.periods = result.periods;
    state.inspect_assessment.periodsBlueprint = {
      blueprint_id: result.blueprint_id,
      blueprint_name: result.blueprint_name,
      template_session_id: result.template_session_id,
    };
    state.inspect_assessment.periodsFetching = false;
    state.inspect_assessment.selectedPeriodIndex = result.periods[0]?.index;
    if (result.period_news) {
      state.inspect_assessment.periodNewsByIndex[
        result.period_news.period_index
      ] = result.period_news;
    }
  },
  failReducer(state, error, payload) {
    if (state.inspect_assessment.periodsAssessmentId !== payload.assessment_id)
      return;
    state.inspect_assessment.periodsFetching = false;
    state.inspect_assessment.periodsError = inspectAssessmentErrorMessage(
      error,
      'Period dates could not be loaded.',
    );
  },
});

export const fetchInspectAssessmentPeriodNews = appManager.createApi<
  { assessment_id: number; period_index: number },
  InspectAssessmentPeriods,
  AppState
>('FETCH_INSPECT_ASSESSMENT_PERIOD_NEWS', {
  path: '/inspect_assessment/periods',
  method: 'GET',
  startReducer(state, payload) {
    state.inspect_assessment.periodNewsFetching = true;
    state.inspect_assessment.periodNewsAssessmentId = payload.assessment_id;
    state.inspect_assessment.periodNewsPeriodIndex = payload.period_index;
    state.inspect_assessment.periodNewsError = undefined;
  },
  successReducer(state, result, payload) {
    if (
      state.inspect_assessment.periodNewsAssessmentId !==
        payload.assessment_id ||
      state.inspect_assessment.periodNewsPeriodIndex !== payload.period_index
    )
      return;
    state.inspect_assessment.periodNewsFetching = false;
    if (!result.period_news) {
      state.inspect_assessment.periodNewsError =
        'The period news response was empty.';
      return;
    }
    state.inspect_assessment.periodNewsByIndex[
      result.period_news.period_index
    ] = result.period_news;
  },
  failReducer(state, error, payload) {
    if (
      state.inspect_assessment.periodNewsAssessmentId !==
        payload.assessment_id ||
      state.inspect_assessment.periodNewsPeriodIndex !== payload.period_index
    )
      return;
    state.inspect_assessment.periodNewsFetching = false;
    state.inspect_assessment.periodNewsError = inspectAssessmentErrorMessage(
      error,
      'Period news could not be loaded.',
    );
  },
});

export const fetchInspectAssessmentPeriodPrices = appManager.createApi<
  {
    assessment_id: number;
    period_index: number;
    instrument_id: number;
    include_news: 'false';
  },
  InspectAssessmentPeriods,
  AppState
>('FETCH_INSPECT_ASSESSMENT_PERIOD_PRICES', {
  path: '/inspect_assessment/periods',
  method: 'GET',
  startReducer(state, payload) {
    const key = inspectAssessmentPeriodPriceKey(
      payload.assessment_id,
      payload.period_index,
      payload.instrument_id,
    );
    state.inspect_assessment.periodPricesFetchingByKey[key] = true;
    delete state.inspect_assessment.periodPricesErrorByKey[key];
  },
  successReducer(state, result, payload) {
    const key = inspectAssessmentPeriodPriceKey(
      payload.assessment_id,
      payload.period_index,
      payload.instrument_id,
    );
    state.inspect_assessment.periodPricesFetchingByKey[key] = false;
    if (state.inspect_assessment.selectedAssessmentId !== payload.assessment_id)
      return;
    if (!result.period_prices) {
      state.inspect_assessment.periodPricesErrorByKey[key] =
        'The period price response was empty.';
      return;
    }
    state.inspect_assessment.periodPricesByKey[key] = result.period_prices;
  },
  failReducer(state, error, payload) {
    const key = inspectAssessmentPeriodPriceKey(
      payload.assessment_id,
      payload.period_index,
      payload.instrument_id,
    );
    state.inspect_assessment.periodPricesFetchingByKey[key] = false;
    if (state.inspect_assessment.selectedAssessmentId !== payload.assessment_id)
      return;
    if (state.inspect_assessment.periodPricesByKey[key]) return;
    state.inspect_assessment.periodPricesErrorByKey[key] =
      inspectAssessmentErrorMessage(
        error,
        'Period prices could not be loaded.',
      );
  },
});

export const fetchInspectAssessmentPeriodBatch = appManager.createApi<
  InspectAssessmentPeriodsBatchRequest,
  InspectAssessmentPeriodsBatch,
  AppState
>('FETCH_INSPECT_ASSESSMENT_PERIOD_BATCH', {
  path: '/inspect_assessment/periods',
  method: 'GET',
  startReducer(state, payload) {
    const periodIndices = inspectAssessmentBatchIds(payload.period_indices);
    const instrumentIds = inspectAssessmentBatchIds(payload.instrument_ids);
    state.inspect_assessment.periodBatchFetching = true;
    state.inspect_assessment.periodBatchAssessmentId = payload.assessment_id;
    state.inspect_assessment.periodBatchRequestKey =
      inspectAssessmentPeriodBatchRequestKey(payload);
    state.inspect_assessment.periodBatchError = undefined;
    periodIndices.forEach(periodIndex => {
      if (payload.include_news === 'true') {
        state.inspect_assessment.periodNewsBatchFetchingByIndex[periodIndex] =
          true;
      }
      instrumentIds.forEach(instrumentId => {
        const key = inspectAssessmentPeriodPriceKey(
          payload.assessment_id,
          periodIndex,
          instrumentId,
        );
        state.inspect_assessment.periodPricesBatchFetchingByKey[key] = true;
        delete state.inspect_assessment.periodPricesErrorByKey[key];
      });
    });
  },
  successReducer(state, result, payload) {
    const requestKey = inspectAssessmentPeriodBatchRequestKey(payload);
    if (
      state.inspect_assessment.periodBatchAssessmentId !==
        payload.assessment_id ||
      state.inspect_assessment.periodBatchRequestKey !== requestKey
    )
      return;

    clearInspectAssessmentBatchFetching(state, payload);
    state.inspect_assessment.periodBatchFetching = false;
    state.inspect_assessment.periodBatchAssessmentId = undefined;
    state.inspect_assessment.periodBatchRequestKey = undefined;

    if (state.inspect_assessment.selectedAssessmentId !== payload.assessment_id)
      return;

    if (result.assessment_id !== payload.assessment_id) {
      state.inspect_assessment.periodBatchError =
        'The background batch returned data for a different assessment.';
      return;
    }

    const returnedPriceKeys = new Set<string>();
    result.period_prices_batch.forEach(periodPrices => {
      const key = inspectAssessmentPeriodPriceKey(
        payload.assessment_id,
        periodPrices.period_index,
        periodPrices.instrument_id,
      );
      returnedPriceKeys.add(key);
      state.inspect_assessment.periodPricesByKey[key] = periodPrices;
      delete state.inspect_assessment.periodPricesErrorByKey[key];
    });
    const returnedNewsIndices = new Set<number>();
    result.period_news_batch.forEach(periodNews => {
      returnedNewsIndices.add(periodNews.period_index);
      state.inspect_assessment.periodNewsByIndex[periodNews.period_index] =
        periodNews;
    });

    const periodIndices = inspectAssessmentBatchIds(payload.period_indices);
    const instrumentIds = inspectAssessmentBatchIds(payload.instrument_ids);
    const missingPricePayload = periodIndices.some(periodIndex =>
      instrumentIds.some(
        instrumentId =>
          !returnedPriceKeys.has(
            inspectAssessmentPeriodPriceKey(
              payload.assessment_id,
              periodIndex,
              instrumentId,
            ),
          ),
      ),
    );
    const missingNewsPayload =
      payload.include_news === 'true' &&
      periodIndices.some(periodIndex => !returnedNewsIndices.has(periodIndex));
    if (missingPricePayload || missingNewsPayload) {
      state.inspect_assessment.periodBatchError =
        'The background batch response was incomplete.';
    }
  },
  failReducer(state, error, payload) {
    const requestKey = inspectAssessmentPeriodBatchRequestKey(payload);
    if (
      state.inspect_assessment.periodBatchAssessmentId !==
        payload.assessment_id ||
      state.inspect_assessment.periodBatchRequestKey !== requestKey
    )
      return;

    clearInspectAssessmentBatchFetching(state, payload);
    state.inspect_assessment.periodBatchFetching = false;
    state.inspect_assessment.periodBatchAssessmentId = undefined;
    state.inspect_assessment.periodBatchRequestKey = undefined;
    if (state.inspect_assessment.selectedAssessmentId !== payload.assessment_id)
      return;
    state.inspect_assessment.periodBatchError = inspectAssessmentErrorMessage(
      error,
      'Background assessment data could not be loaded.',
    );
  },
});

export const selectInspectAssessmentPeriod = appManager.createLocalEvent(
  'SELECT_INSPECT_ASSESSMENT_PERIOD',
  (state: AppState, periodIndex?: number) => {
    state.inspect_assessment.selectedPeriodIndex = periodIndex;
  },
);
